/**
 * Diagnosis-Driven Question Generation
 * 
 * Extracts and prioritizes clarifying questions from the differential diagnosis data.
 * Questions are generated from:
 * 1. whatWouldRuleOut - Questions that help EXCLUDE unlikely diagnoses
 * 2. keyFindings/supporting_factors - Questions that help CONFIRM likely diagnoses
 * 
 * Questions are prioritized by diagnosis likelihood (higher likelihood = asked first)
 */

import { FILLER_WORDS } from './constants';

// =============================================================================
// QUESTION EXTRACTION
// =============================================================================

/**
 * Extract questions from a single diagnosis
 * 
 * @param {Object} diagnosis - Diagnosis object from LLM response
 * @returns {Array<{question: string, type: string, diagnosisLikelihood: number, diagnosisName: string}>}
 */
const extractQuestionsFromDiagnosis = (diagnosis) => {
  const questions = [];
  const likelihood = diagnosis.likelihood || 50;
  const diagnosisName = diagnosis.condition || 'Unknown';


  // PRIMARY SOURCE: Use LLM-generated refinement questions (preferred)
  // These are already patient-friendly and diagnosis-specific
  if (diagnosis.refinementQuestions && Array.isArray(diagnosis.refinementQuestions)) {
    diagnosis.refinementQuestions.forEach((question, index) => {
      if (question && typeof question === 'string' && question.length > 5) {
        // Clean up the question
        let cleanedQuestion = question.trim();
        if (!cleanedQuestion.endsWith('?')) {
          cleanedQuestion += '?';
        }
        // Capitalize first letter
        cleanedQuestion = cleanedQuestion.charAt(0).toUpperCase() + cleanedQuestion.slice(1);
        
        questions.push({
          question: cleanedQuestion,
          type: 'refinement',
          originalText: question,
          diagnosisLikelihood: likelihood,
          diagnosisName,
          priority: likelihood - (index * 0.5),  // Higher likelihood diagnoses get priority
        });
      }
    });
  }

  // FALLBACK: If no refinement questions, try to convert whatWouldRuleOut
  if (questions.length === 0 && diagnosis.whatWouldRuleOut) {
    const ruleOutText = diagnosis.whatWouldRuleOut;
    let formattedQuestion = convertToPatientQuestion(ruleOutText, 'ruleOut');
    
    // If conversion failed, try a more aggressive approach
    if (!formattedQuestion) {
      formattedQuestion = createAggressiveQuestion(ruleOutText, diagnosisName);
    }
    
    if (formattedQuestion) {
      questions.push({
        question: formattedQuestion,
        type: 'ruleOut',
        originalText: ruleOutText,
        diagnosisLikelihood: likelihood,
        diagnosisName,
      });
    } else {
    }
  }

  // SECONDARY FALLBACK: Try keyFindings and supporting_factors
  if (questions.length === 0) {
    // Extract from keyFindings
    if (diagnosis.keyFindings && Array.isArray(diagnosis.keyFindings)) {
      diagnosis.keyFindings.forEach((finding, index) => {
        if (finding && finding.length > 3 && !isEstablishedFinding(finding)) {
          let formattedQuestion = convertToPatientQuestion(finding, 'keyFinding');
          if (!formattedQuestion) {
            formattedQuestion = createAggressiveQuestion(finding, diagnosisName);
          }
          if (formattedQuestion) {
            questions.push({
              question: formattedQuestion,
              type: 'keyFinding',
              originalText: finding,
              diagnosisLikelihood: likelihood,
              diagnosisName,
              priority: likelihood - (index * 0.5),
            });
          }
        }
      });
    }

    // Extract from supporting_factors
    if (diagnosis.supporting_factors && Array.isArray(diagnosis.supporting_factors)) {
      diagnosis.supporting_factors.forEach((factor, index) => {
        if (factor && factor.length > 3 && !isEstablishedFinding(factor)) {
          let formattedQuestion = convertToPatientQuestion(factor, 'supportingFactor');
          if (!formattedQuestion) {
            formattedQuestion = createAggressiveQuestion(factor, diagnosisName);
          }
          if (formattedQuestion) {
            questions.push({
              question: formattedQuestion,
              type: 'supportingFactor',
              originalText: factor,
              diagnosisLikelihood: likelihood,
              diagnosisName,
              priority: likelihood - (index * 0.5) - 10,
            });
          }
        }
      });
    }
  }

  return questions;
};

/**
 * Aggressive question creation - used when normal conversion fails
 * Tries harder to create a patient-friendly question from clinical text
 */
const createAggressiveQuestion = (clinicalText, diagnosisName) => {
  if (!clinicalText || typeof clinicalText !== 'string') return null;
  
  const text = clinicalText.trim().toLowerCase();
  
  // Skip if it clearly requires medical tests/exams that patient can't answer
  const hardSkipPatterns = [
    'ecg', 'ekg', 'x-ray', 'xray', 'ct scan', 'ct-scan', 'mri', 'ultrasound',
    'blood test', 'lab test', 'biopsy', 'culture', 'imaging', 'scan',
    'physical exam', 'auscultation', 'palpation', 'percussion',
    'troponin', 'cbc', 'cmp', 'bmp', 'd-dimer', 'crp', 'esr',
    'urine test', 'stool test', 'lumbar puncture', 'spinal tap',
  ];
  
  if (hardSkipPatterns.some(pattern => text.includes(pattern))) {
    return null;
  }
  
  // Common symptom keywords to extract and ask about
  const symptomKeywords = {
    'fever': 'Have you had a fever or felt feverish?',
    'chills': 'Have you had any chills?',
    'sweating': 'Have you been sweating more than usual, especially at night?',
    'nausea': 'Have you felt nauseous?',
    'vomiting': 'Have you vomited?',
    'diarrhea': 'Have you had diarrhea?',
    'constipation': 'Have you been constipated?',
    'headache': 'Have you had any headaches?',
    'dizziness': 'Have you felt dizzy or lightheaded?',
    'fatigue': 'Have you been feeling unusually tired or fatigued?',
    'weakness': 'Have you noticed any weakness?',
    'pain': 'Can you tell me more about where exactly you feel pain?',
    'swelling': 'Have you noticed any swelling?',
    'rash': 'Have you noticed any rash or skin changes?',
    'cough': 'Have you had a cough?',
    'shortness of breath': 'Have you had any difficulty breathing?',
    'chest pain': 'Have you had any chest pain or discomfort?',
    'palpitations': 'Have you noticed your heart racing or skipping beats?',
    'numbness': 'Have you experienced any numbness or tingling?',
    'vision': 'Have you noticed any changes in your vision?',
    'hearing': 'Have you had any changes in your hearing?',
    'appetite': 'Has your appetite changed recently?',
    'weight loss': 'Have you lost weight recently without trying?',
    'weight gain': 'Have you gained weight recently?',
    'sleep': 'Have you had trouble sleeping?',
    'urination': 'Have you noticed any changes in urination?',
    'blood in urine': 'Have you noticed any blood in your urine?',
    'blood in stool': 'Have you noticed any blood in your stool?',
    'abdominal': 'Can you point to where in your abdomen you feel discomfort?',
    'back pain': 'Have you had any back pain?',
    'joint': 'Have you had any joint pain or stiffness?',
    'muscle': 'Have you had any muscle aches?',
    'anxiety': 'Have you been feeling anxious or worried?',
    'depression': 'Have you been feeling down or depressed?',
    'confusion': 'Have you felt confused or had trouble thinking clearly?',
    'memory': 'Have you had any memory problems?',
    'trauma': 'Have you had any recent injuries or accidents?',
    'fall': 'Have you fallen recently?',
    'stress': 'Have you been under a lot of stress lately?',
    'travel': 'Have you traveled anywhere recently?',
    'contact': 'Have you been around anyone who was sick?',
    'exposure': 'Have you been exposed to anything unusual recently?',
    'medication': 'Have you started any new medications recently?',
    'allergy': 'Do you have any allergies that might be relevant?',
    'family history': 'Does anyone in your family have similar problems?',
  };
  
  // Check for symptom keywords
  for (const [keyword, question] of Object.entries(symptomKeywords)) {
    if (text.includes(keyword)) {
      return question;
    }
  }
  
  // If the text is asking about absence of something, convert to a yes/no question
  if (text.includes('absence of') || text.includes('no ') || text.includes('without ')) {
    // Extract what's being negated
    const absenceMatch = text.match(/(?:absence of|no |without )(.+?)(?:\.|,|$)/);
    if (absenceMatch) {
      const symptom = absenceMatch[1].trim();
      if (symptom.length > 2 && symptom.length < 50) {
        return `Have you experienced ${symptom}?`;
      }
    }
  }
  
  // If text mentions "history of", ask about past occurrences
  if (text.includes('history of') || text.includes('previous')) {
    const historyMatch = text.match(/(?:history of|previous )(.+?)(?:\.|,|$)/);
    if (historyMatch) {
      const condition = historyMatch[1].trim();
      if (condition.length > 2 && condition.length < 50) {
        return `Have you had ${condition} before?`;
      }
    }
  }
  
  // Generic fallback: if it's a short, simple phrase, ask about it
  if (text.length > 5 && text.length < 60 && !text.includes('test') && !text.includes('exam')) {
    // Clean up the text
    let cleaned = clinicalText.trim()
      .replace(/^(if |when |presence of |absence of )/i, '')
      .replace(/\.$/, '');
    
    if (cleaned.length > 3 && cleaned.length < 50) {
      return `Have you noticed or experienced ${cleaned.toLowerCase()}?`;
    }
  }
  
  return null;
};

/**
 * Check if a finding is already established from patient's initial statement
 * These shouldn't be asked as questions since we already know the answer
 */
const isEstablishedFinding = (finding) => {
  const lowerFinding = finding.toLowerCase();
  
  // Skip very common/vague terms that are likely already mentioned
  const skipPatterns = [
    'patient reports',
    'as reported',
    'stated above',
    'mentioned',
    'chief complaint',
    'presents with',
  ];
  
  return skipPatterns.some(pattern => lowerFinding.includes(pattern));
};

// =============================================================================
// CLINICAL TO PATIENT-FRIENDLY CONVERSION
// =============================================================================

/**
 * Convert a clinical finding or statement to a patient-friendly question
 * 
 * @param {string} clinicalText - Clinical text (e.g., "Absence of rash would rule out allergic reaction")
 * @param {string} type - Type: 'ruleOut', 'keyFinding', or 'supportingFactor'
 * @returns {string|null} - Patient-friendly question or null if not convertible
 */
const convertToPatientQuestion = (clinicalText, type) => {
  if (!clinicalText || typeof clinicalText !== 'string') return null;
  
  const text = clinicalText.trim();
  if (text.length < 5) return null;

  // If it's already a question, clean it up and return
  if (text.endsWith('?')) {
    return cleanupQuestion(text);
  }

  // Conversion patterns based on type
  if (type === 'ruleOut') {
    return convertRuleOutToQuestion(text);
  } else if (type === 'keyFinding' || type === 'supportingFactor') {
    return convertFindingToQuestion(text);
  }

  return null;
};

/**
 * Convert a "what would rule out" statement to a question
 * Examples:
 * - "Absence of fever" → "Have you had a fever?"
 * - "No history of similar episodes" → "Have you had similar episodes before?"
 * - "ECG showing normal rhythm" → null (requires testing, not a question)
 */
const convertRuleOutToQuestion = (text) => {
  const lower = text.toLowerCase();

  // Skip things that require medical tests (can't ask patient directly)
  const requiresTestPatterns = [
    'ecg', 'ekg', 'x-ray', 'xray', 'ct scan', 'mri', 'ultrasound',
    'blood test', 'lab', 'biopsy', 'culture', 'imaging',
    'physical exam', 'auscultation', 'palpation',
    'troponin', 'cbc', 'cmp', 'bmp', 'd-dimer',
  ];
  
  if (requiresTestPatterns.some(pattern => lower.includes(pattern))) {
    return null;
  }

  // Pattern: "Absence of X" → "Have you noticed X?" or "Do you have X?"
  const absenceMatch = lower.match(/absence of (.+)/);
  if (absenceMatch) {
    return `Have you noticed any ${absenceMatch[1]}?`;
  }

  // Pattern: "No X" → "Do you have X?"
  const noMatch = lower.match(/^no (.+)/);
  if (noMatch) {
    const symptom = noMatch[1].replace(/\.$/, '');
    return `Have you experienced ${symptom}?`;
  }

  // Pattern: "Negative for X" → "Have you had X?"
  const negativeMatch = lower.match(/negative for (.+)/);
  if (negativeMatch) {
    return `Have you had ${negativeMatch[1]}?`;
  }

  // Pattern: "History of X" → "Have you had X before?"
  const historyMatch = lower.match(/history of (.+)/);
  if (historyMatch) {
    return `Have you had ${historyMatch[1]} before?`;
  }

  // Pattern: "If X, less likely" → "Do you have X?"
  const ifPattern = lower.match(/if (?:patient (?:has|reports|denies) )?(.+?)(?:,| -| would)/);
  if (ifPattern) {
    const condition = ifPattern[1].replace(/^no /, '').trim();
    return `Have you experienced ${condition}?`;
  }

  // Pattern: Specific symptom question conversion
  const symptomConversions = [
    { pattern: /fever/, question: "Have you had a fever or felt feverish?" },
    { pattern: /chest pain radiating/, question: "Does the chest pain spread to your arm, neck, or jaw?" },
    { pattern: /shortness of breath|dyspnea/, question: "Are you having difficulty breathing or feeling short of breath?" },
    { pattern: /nausea/, question: "Have you felt nauseous or like you might vomit?" },
    { pattern: /vomiting/, question: "Have you vomited?" },
    { pattern: /diarrhea/, question: "Have you had diarrhea?" },
    { pattern: /headache/, question: "Have you had a headache?" },
    { pattern: /dizziness|lightheaded/, question: "Have you felt dizzy or lightheaded?" },
    { pattern: /weakness/, question: "Have you noticed any weakness?" },
    { pattern: /fatigue|tired/, question: "Have you been feeling unusually tired?" },
    { pattern: /rash/, question: "Have you noticed any rash or skin changes?" },
    { pattern: /swelling|edema/, question: "Have you noticed any swelling?" },
    { pattern: /numbness|tingling/, question: "Have you experienced any numbness or tingling?" },
    { pattern: /vision change/, question: "Have you had any changes in your vision?" },
    { pattern: /speech/, question: "Have you had any difficulty speaking?" },
    { pattern: /confusion/, question: "Have you felt confused or disoriented?" },
    { pattern: /weight loss/, question: "Have you lost weight recently?" },
    { pattern: /appetite/, question: "Has your appetite changed?" },
    { pattern: /sleep/, question: "Have you had trouble sleeping?" },
    { pattern: /travel/, question: "Have you traveled anywhere recently?" },
    { pattern: /sick contact|exposure/, question: "Have you been around anyone who's been sick?" },
  ];

  for (const { pattern, question } of symptomConversions) {
    if (pattern.test(lower)) {
      return question;
    }
  }

  // Generic fallback: If text is a simple symptom phrase, ask about it
  if (text.length < 50 && !lower.includes('test') && !lower.includes('exam')) {
    return `Have you experienced ${text.toLowerCase().replace(/\.$/, '')}?`;
  }

  return null;
};

/**
 * Convert a key finding or supporting factor to a confirmatory question
 * These are things the patient has that support a diagnosis - we ask to confirm
 */
const convertFindingToQuestion = (text) => {
  const lower = text.toLowerCase();

  // Skip things that are already confirmed or require testing
  const skipPatterns = [
    'patient report', 'as stated', 'reported', 'mentioned',
    'test', 'exam', 'lab', 'imaging', 'shows', 'reveals',
  ];
  
  if (skipPatterns.some(pattern => lower.includes(pattern))) {
    return null;
  }

  // If it's a short symptom phrase, ask about related symptoms
  // e.g., "chest pain" → We might ask about radiation, timing, triggers
  const relatedQuestions = {
    'chest pain': "Does anything make the chest pain better or worse?",
    'headache': "Is the headache accompanied by any light sensitivity or nausea?",
    'abdominal pain': "Can you point to exactly where the pain is?",
    'cough': "Is the cough producing any mucus or phlegm?",
    'fever': "Have you checked your temperature? What was it?",
    'fatigue': "When did the fatigue start?",
    'nausea': "Have you been able to keep food and water down?",
    'dizziness': "Do you feel dizzy when you stand up, or all the time?",
    'shortness of breath': "Does the shortness of breath happen at rest or only with activity?",
    'pain': "Can you describe the pain - is it sharp, dull, aching, or burning?",
  };

  for (const [symptom, question] of Object.entries(relatedQuestions)) {
    if (lower.includes(symptom)) {
      return question;
    }
  }

  // Don't ask generic questions about findings - they're too leading
  return null;
};

/**
 * Clean up and normalize a question
 */
const cleanupQuestion = (question) => {
  let cleaned = question.trim();
  
  // Remove clinical jargon prefixes
  cleaned = cleaned.replace(/^(please|kindly|could you|would you|can you)\s+/i, '');
  
  // Ensure proper capitalization
  cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  
  // Ensure ends with question mark
  if (!cleaned.endsWith('?')) {
    cleaned += '?';
  }
  
  return cleaned;
};

// =============================================================================
// QUESTION PRIORITIZATION AND DEDUPLICATION
// =============================================================================

/**
 * Extract all questions from visualization data and sort by priority
 * 
 * @param {Object} visualizationData - Full response from LLM with diagnoses
 * @returns {Array} - Sorted array of question objects
 */
export const extractPrioritizedQuestions = (visualizationData) => {
  if (!visualizationData) return [];

  const allQuestions = [];

  // New format: single diagnoses array (current format)
  if (visualizationData.diagnoses && Array.isArray(visualizationData.diagnoses)) {
    visualizationData.diagnoses.forEach(diagnosis => {
      const questions = extractQuestionsFromDiagnosis(diagnosis);
      allQuestions.push(...questions);
    });
  }

  // Old format: primary and alternative arrays (backward compatibility)
  if (visualizationData.primary_diagnoses && Array.isArray(visualizationData.primary_diagnoses)) {
    visualizationData.primary_diagnoses.forEach(diagnosis => {
      const questions = extractQuestionsFromDiagnosis(diagnosis);
      // Boost primary diagnosis questions
      questions.forEach(q => {
        q.priority = (q.priority || q.diagnosisLikelihood) + 20;
      });
      allQuestions.push(...questions);
    });
  }

  if (visualizationData.alternative_diagnoses && Array.isArray(visualizationData.alternative_diagnoses)) {
    visualizationData.alternative_diagnoses.forEach(diagnosis => {
      const questions = extractQuestionsFromDiagnosis(diagnosis);
      allQuestions.push(...questions);
    });
  }

  // Deduplicate similar questions
  const dedupedQuestions = deduplicateQuestions(allQuestions);

  // Sort by priority (highest first)
  dedupedQuestions.sort((a, b) => {
    const priorityA = a.priority || a.diagnosisLikelihood || 0;
    const priorityB = b.priority || b.diagnosisLikelihood || 0;
    return priorityB - priorityA;
  });


  return dedupedQuestions;
};

/**
 * Remove duplicate or very similar questions
 */
const deduplicateQuestions = (questions) => {
  const seen = new Set();
  const result = [];

  for (const q of questions) {
    const normalized = normalizeForDedup(q.question);
    
    if (!seen.has(normalized)) {
      seen.add(normalized);
      result.push(q);
    }
  }

  return result;
};

/**
 * Normalize a question for deduplication comparison
 * Removes filler words and normalizes to core meaning
 */
const normalizeForDedup = (question) => {
  return question
    .toLowerCase()
    .replace(/[?.,!]/g, '')
    .split(/\s+/)
    .filter(word => !FILLER_WORDS.has(word))
    .sort()
    .join(' ');
};

// =============================================================================
// QUESTION FILTERING (exclude already-asked)
// =============================================================================

/**
 * Filter questions that haven't been asked yet
 * Uses fuzzy matching similar to existing isQuestionAlreadyAsked
 * 
 * @param {Array} questions - Array of question objects
 * @param {Array} askedQuestions - Array of already-asked question strings
 * @returns {Array} - Filtered array of unasked questions
 */
export const filterUnaskedQuestions = (questions, askedQuestions) => {
  if (!questions || !Array.isArray(questions)) return [];
  if (!askedQuestions || !Array.isArray(askedQuestions) || askedQuestions.length === 0) {
    return questions;
  }

  return questions.filter(q => {
    const qNormalized = normalizeForDedup(q.question);
    
    return !askedQuestions.some(asked => {
      const askedNormalized = normalizeForDedup(asked);
      
      // Check for high overlap (fuzzy match)
      const qWords = new Set(qNormalized.split(' '));
      const askedWords = new Set(askedNormalized.split(' '));
      
      // Count common words
      let commonCount = 0;
      for (const word of qWords) {
        if (askedWords.has(word)) commonCount++;
      }
      
      // If more than 70% overlap, consider it already asked
      const overlapRatio = commonCount / Math.max(qWords.size, askedWords.size);
      return overlapRatio > 0.7;
    });
  });
};

/**
 * Get the next best question to ask
 * 
 * @param {Object} visualizationData - Full diagnosis data from LLM
 * @param {Array} askedQuestions - Array of already-asked question strings
 * @returns {Object|null} - Next question object or null if none available
 */
export const getNextDiagnosisQuestion = (visualizationData, askedQuestions = []) => {
  const allQuestions = extractPrioritizedQuestions(visualizationData);
  const unaskedQuestions = filterUnaskedQuestions(allQuestions, askedQuestions);
  
  if (unaskedQuestions.length > 0) {
    const next = unaskedQuestions[0];
    return next;
  }
  
  return null;
};

/**
 * Check if we have diagnosis-driven questions available
 * 
 * @param {Object} visualizationData - Full diagnosis data from LLM
 * @returns {boolean}
 */
export const hasDiagnosisQuestions = (visualizationData) => {
  if (!visualizationData) return false;
  
  // New format: single diagnoses array
  const hasDiagnoses = visualizationData.diagnoses?.length > 0;
  
  // Old format: primary and alternative arrays (backward compatibility)
  const hasPrimary = visualizationData.primary_diagnoses?.length > 0;
  const hasAlternative = visualizationData.alternative_diagnoses?.length > 0;
  
  return hasDiagnoses || hasPrimary || hasAlternative;
};

export default {
  extractPrioritizedQuestions,
  filterUnaskedQuestions,
  getNextDiagnosisQuestion,
  hasDiagnosisQuestions,
};
